import {CanDeactivateFn} from '@angular/router';
import {inject} from "@angular/core";
import {FormService} from "../../services/form.service";
import {NavService} from "../../services/nav.service";
import {FormStateEnum} from "../../libs/form/items/form-state.enum";
import {DeactivateDialogService} from "./deactivate-dialog.service";
import {filter, finalize, takeUntil} from "rxjs/operators";
import {Subject, Subscription, timer} from "rxjs";
import {unsubscribe} from "../../libs/utils/epic.utils";

export const formStateDeactivateGuard: CanDeactivateFn<any> = async (): Promise<boolean> => {

  const formService = inject(FormService);
  const destroy = new Subject<void>();

  /*
   * true = OK, the page may be changed. Either everything is saved or the user wants to leave the page without saving.
   * false = Leaving the page is canceled. The user remains on the current page.
   */
  if (formService.hasWrapper() && [FormStateEnum.CHANGED, FormStateEnum.WORKING].includes(formService.getWrapperState())) {
    const navService = inject(NavService);
    const dialog = inject(DeactivateDialogService);
    const timeout = timer(10000);
    let completedByTimeout = false;
    const subs: Subscription[] = [];
    subs.push(destroy.subscribe(() => unsubscribe(subs)));

    if (dialog.isClosed()) {
      return new Promise<boolean>((resolve) => {
        dialog.openLeave().then((result: boolean) => {
          if (!result) {
            navService.rollback();
          }
          destroy.next();
          destroy.complete();
          resolve(result);
        });
        subs.push(
          timeout.subscribe(() => completedByTimeout = true),
          formService.getWrapperStateChange()
            .pipe(
              takeUntil(destroy),
              takeUntil(timeout),
              filter((state) => state === FormStateEnum.SUCCESS),
              finalize(() => {
                destroy.next();
                destroy.complete();
              }))
            .subscribe({
              next: () => {
                dialog.close();
                resolve(true);
              },
              complete: () => {
                if (completedByTimeout) {
                  dialog.close();
                  navService.rollback();
                  resolve(false);
                }
              }
            })
        );
      });
    }
    // Already opened
    return Promise.resolve(false);
  }
  // else
  destroy.next();
  destroy.complete();
  return Promise.resolve(true);
};
